/**
 * 模拟交易类型定义
 *
 * @author page
 * @date 2026-04-21
 */

/**
 * 模拟账户
 */
export interface SimulateAccount {
    id: number;
    userId?: number;
    initialBalance: string;
    availableBalance: string;
    totalPositionValue?: string;
    frozenBalance?: string;
    unrealizedPnl?: string;
    realizedPnl?: string;
    cumFundingFee?: string;
    cumTradeFee?: string;
    mode?: number;           // 0-现货 1-合约 2-混合
    runStartTime: string;
    runEndTime: string;
    maxLeverage?: number;
    status: number;             // 0-正常 1-暂停 2-已归档
    createdAt: string;
    updatedAt?: string;
}

/**
 * 模拟订单（新）
 */
export interface SimulateOrder {
    id: number;
    accountId: number;
    userId: number;
    exchange: string;
    symbol: string;
    side: number;           // 0-买 1-卖
    type: number;          // 0-市价 1-限价
    price?: string;
    stopPrice?: string;
    quantity: string;
    filledQuantity: string;
    avgFillPrice?: string;
    fee: string;
    status: number;         // 0-待成交 1-部分成交 2-已成交 3-已取消 4-已拒绝 5-已过期
    positionId?: number;    // 关联的持仓ID
    idemKey?: string;
    orderTime: string;
    fillTime?: string;
    updateTime?: string;
    createdAt?: string;
}

/**
 * 模拟持仓
 */
export interface SimulatePosition {
    id?: number;
    accountId?: number;
    userId?: number;
    exchange?: string;
    symbol: string;
    mode?: number;        // 0-现货 1-合约
    side: number;         // 0-多 1-空
    status?: number;      // 0-激活 1-已平仓
    quantity: string;
    avgPrice: string;
    unrealizedPnl: string;
    cumFundingFee: string;
    leverage?: number;
    margin?: string;
    liquidationPrice?: string;
    realizedPnl?: string;
    cumTradeFee?: string;
    openTime?: string;
    updateTime?: string;
    createdAt?: string;
}

/**
 * 模拟交易记录
 */
export interface SimulateTrade {
    id: number;
    orderId?: number;
    accountId?: number;
    userId?: number;
    exchange?: string;
    symbol: string;
    side: number;           // 0-买 1-卖
    type: number;           // 0-市价 1-限价
    price: string;
    quantity: string;
    quoteAmount?: string;
    fee: string;
    fundingRate?: string;
    orderTime?: string;
    tradeTime?: string;
    createdAt?: string;
}

/**
 * 资金流水
 */
export interface SimulateLedger {
    id: number;
    accountId: number;
    userId: number;
    symbol?: string;
    eventType: number;
    amount: string;
    balanceAfter: string;
    refId?: number;
    idemKey?: string;
    memo?: string;
    createTime: string;
    createdAt?: string;
}

/**
 * 结算事件
 */
export interface SettlementEvent {
    id: number;
    accountId: number;
    userId: number;
    eventType: number;      // 0-定时 1-手动
    settlementType: number; // 0-资金费率 1-止盈止损 2-定时 3-恢复
    status: number;         // 0-待处理 1-进行中 2-已完成 3-失败
    triggerTime: string;
    processStartTime?: string;
    processEndTime?: string;
    totalPositions: number;
    totalFundingAmount: string;
    totalRealizedPnl: string;
    totalFee: string;
    errorMessage?: string;
    createdAt?: string;
}

/**
 * 收益统计
 */
/**
 * K线数据
 */
export interface KlineData {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

/**
 * 指标的单个实例配置
 * 每个指标可包含多个实例，每个实例独立配置周期
 */
export interface IndicatorInstanceConfig {
    /** 是否启用 */
    enabled: boolean;
    /** 周期列表（1-3个值） */
    periods: number[];
    /** BOLL标准差 */
    stdDev?: number;
    /** 指标线颜色（自动分配后持久化） */
    color?: string;
}

/**
 * 指标配置映射
 * key: 指标名(EMA/BOLL/MACD等)
 * value: 实例列表
 */
export type IndicatorConfigMap = Record<string, IndicatorInstanceConfig[]>;

/**
 * 技术指标数据点
 */
export interface IndicatorDataPoint {
    timestamp: number;
    value?: number;
    upper?: number;
    middle?: number;
    lower?: number;
    signal?: number;
    histogram?: number;
    diff?: number;
    dea?: number;
    macd?: number;
    k?: number;
    d?: number;
    j?: number;
}

/**
 * 技术指标集合（按指标类型分组）
 * key 格式: "EMA_ema_12", "MACD_12_26_9", "BOLL_20_2" 等
 */
export type TechnicalIndicators = Record<string, IndicatorDataPoint[]>;

/**
 * K线与指标响应数据
 */
export interface KlineWithIndicatorsResponse {
    kline: KlineData[];
    indicators?: Record<string, Array<{
        period: string;
        values: Array<{
            timestamp: number;
            value?: number;
            upper?: number;
            middle?: number;
            lower?: number;
            k?: number;
            d?: number;
            j?: number;
            diff?: number;
            dea?: number;
            macd?: number;
        }>;
    }>>;
}

/**
 * 交易所配置项
 */
export interface ExchangeConfig {
    id: number;
    exchangeName: string;  // 交易所：OKX/Bybit/Hyperliquid
    enabled: number;       // 0-启用 1-禁用
    sortOrder: number;     // 排序顺序
    createdAt: string;
    updatedAt: string;
}

/**
 * 品种配置记录（新版，独立表模式）
 */
export interface SymbolConfigRecord {
    id: number;           // 主键ID
    exchangeName: string; // 交易所：OKX/Bybit/Hyperliquid
    productType: string;  // 产品类型：contract-合约 spot-现货
    symbol: string;       // 交易对：BTC/USDT
    coinSymbol?: string;  // 真实交易符号：如 BTC/USDT:USDT（永续合约）
    enabled: number;      // 0-启用 1-禁用
    sortOrder: number;    // 排序顺序
    createdAt: string;
    updatedAt: string;
}

/**
 * 支持的交易所常量
 */
export const SUPPORTED_EXCHANGES = ['OKX', 'Bybit', 'Hyperliquid'] as const;

/**
 * 支持的交易类型常量
 */
export const SUPPORTED_PRODUCT_TYPES = [
    {key: 'contract', label: '合约'},
    {key: 'spot', label: '现货'},
] as const;

// ==================== 常量 ====================

/**
 * 交易方向
 */
export const TradeDirection = {
    BUY: 0,
    SELL: 1,
} as const;

/**
 * 订单类型
 */
export const OrderType = {
    MARKET: 0,
    LIMIT: 1,
} as const;

/**
 * 订单状态
 */
export const OrderStatus = {
    PENDING: 0,
    PARTIAL: 1,
    FILLED: 2,
    CANCELLED: 3,
    REJECTED: 4,
} as const;

/**
 * 账户状态
 */
export const AccountStatus = {
    NORMAL: 0,
    DISABLED: 1,
} as const;

/**
 * 预设可选币种对
 */
export const AVAILABLE_TRADING_PAIRS = [
    'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT',
    'XRP/USDT', 'DOGE/USDT', 'ADA/USDT', 'AVAX/USDT',
    'DOT/USDT', 'LINK/USDT', 'MATIC/USDT', 'ATOM/USDT',
] as const;

/**
 * 技术指标类型调色板（各类型预设颜色，取自项目主题色系）
 * 每种类型有独立色系，确保暗色背景下的可见性和类型间区分度
 */
export const INDICATOR_COLORS: Record<string, string> = {
    EMA: '#4d9fff',
    SMA: '#eb2f96',
    WMA: '#00d97e',
    RSI: '#9254de',
    MACD: '#fa541c',
    BOLL: '#f5222d',
    KDJ: '#f5a623',
    CCI: '#13c2c2',
    ATR: '#f5a623',
    OBV: '#2f54eb',
    ADX: '#f5222d',
};

/** 指标类型调色板/颜色数组（用于多实例时自动循环分配） */
export const INDICATOR_PALETTES: Record<string, string[]> = {
    EMA: ['#4d9fff', '#00b4d8', '#0077b6', '#48cae4'],
    SMA: ['#eb2f96', '#fa541c', '#fadb14', '#a8071a'],
    WMA: ['#00d97e', '#73d13d', '#95de64', '#b7eb8f'],
    RSI: ['#9254de', '#d46b08', '#389e0d', '#0958d9'],
    MACD: ['#fa541c', '#722ed1', '#13c2c2', '#00d97e'],
    BOLL: ['#f5222d', '#ff5252', '#ff7875', '#ffa39e'],
    KDJ: ['#f5a623', '#13c2c2', '#eb2f96', '#9254de'],
    CCI: ['#13c2c2', '#36cfc9', '#5cdbd3', '#87e8de'],
    ATR: ['#f5a623', '#ffa940', '#ffc06d', '#ffd591'],
    OBV: ['#2f54eb', '#597ef7', '#85a5ff', '#adc6ff'],
    ADX: ['#f5222d', '#ff5252', '#ff7875', '#ffa39e'],
};

/** 默认回退调色板 */
const FALLBACK_PALETTE = ['#F0B429', '#00d97e', '#00d97e', '#f5a623'];

/**
 * 获取某个指标类型的下一个自动分配颜色
 * @param type 指标类型
 * @param existingColors 该类型已有实例的颜色列表
 */
export const getNextIndicatorColor = (type: string, existingColors: string[]): string => {
    const palette = INDICATOR_PALETTES[type] || FALLBACK_PALETTE;
    const usedSet = new Set(existingColors);
    const unused = palette.find(c => !usedSet.has(c));
    if (unused) return unused;
    return palette[existingColors.length % palette.length];
};
